<template>
  <div class="history-modal">
    <div class="history-modal__main">
      <HistList
        :list="normalizedList"
        :loading="computedLoading"
        :model-name="modelName"
        :model-id="modelId"
        :subject="subject"
        :on-export="modelName === 'audit_trail' ? downloadAuditTrail : null"
        :export-pending="exportPending"
        :on-query-change="resolvedOnQueryChange"
        :server-total="serverTotal"
        :server-authors="serverAuthors"
        ref="listRef"
        @update:records-count="onRecordsCount"
      />
    </div>

    <div class="history-modal__footer">
      <div class="modal-footer-info">
        <i class="bi bi-info-circle" aria-hidden="true"></i>
        <span>{{ t('recordsCount', { n: recordsCount }) }}</span>
      </div>
      <button
        type="button"
        class="btn page-header-btn page-header-btn--primary"
        @click="$emit('modalstack:close', false)"
      >
        {{ t('common.ok') }}
      </button>
    </div>
  </div>
</template>
<script setup>
import { computed, toValue, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { format } from 'date-fns'
import { useApi } from 'soykajs/composables/api'
import { useMessageStack } from '@/stores/messagestack'
import { saveBlob } from '@/lib/download'
import HistList from '@/components/history/list.vue'

const { t } = useI18n()
const { push } = useMessageStack()
defineEmits(['modalstack:close'])
const props = defineProps({
  list: {
    type: [Object, Array],
    default: () => [],
  },
  loading: { type: [Boolean, Object], default: true },
  modelName: String,
  modelId: Number,
  subject: { type: String, default: '' },
  onSelect: { type: Function, default: null },
  /**
   * Текущий модуль AT (ltms / catalogs / docparser). Смена в шапке modalstack
   * идёт в onSelect родителя, минуя модалку — Excel берёт service отсюда,
   * иначе Досье выгружается как ltms (пустая таблица).
   */
  auditService: { type: [String, Object], default: 'ltms' },
  /** Audit Trail досье: серверная пагинация/фильтры. */
  onQueryChange: { type: Function, default: null },
  serverTotal: { type: [Number, Object], default: null },
  serverAuthors: { type: [Array, Object], default: null },
})
const listRef = ref()

// Через useApi, а не fetch: soykajs ставит Authorization и X-Locale — backend
// собирает xlsx на языке переключателя; не-JSON ответ приходит в model.bytes
const {
  model: exportFile,
  pending: exportPending,
  refresh: fetchAuditTrailExport,
} = useApi({ url: 'audit_trail/export', immediate: false })

async function downloadAuditTrail() {
  const query = { service: toValue(props.auditService) || 'ltms' }
  const ext = listRef.value?.getExportParams?.()
  if (ext?.authors?.length) {
    query.authors = ext.authors
  }
  if (ext?.dateFrom) {
    query.date_from = format(new Date(ext.dateFrom), 'yyyy-MM-dd')
  }
  if (ext?.dateTo) {
    query.date_to = format(new Date(ext.dateTo), 'yyyy-MM-dd')
  }

  try {
    await fetchAuditTrailExport({ query })
  } catch {
    push(t('exportFailed'))
    return
  }
  const bytes = exportFile.value?.bytes
  if (!bytes) {
    push(t('downloadFailed'))
    return
  }
  saveBlob(bytes, t('auditTrailFileName', { date: format(new Date(), 'dd.MM.yyyy') }), 'xlsx')
  exportFile.value = null
}

const recordsCount = ref(0)
defineExpose({
  listRef,
})
const computedLoading = computed(() =>
  typeof props.loading === 'boolean' ? props.loading : toValue(props.loading)
)
const resolvedOnQueryChange = computed(() => toValue(props.onQueryChange) || null)
const normalizedList = computed(() => {
  const val = toValue(props.list)
  if (!val) return { history: [], related: {} }
  const data =
    val?.data && typeof val.data === 'object' && !Array.isArray(val.data) ? val.data : val
  const history = Array.isArray(data?.history) ? data.history : Array.isArray(data) ? data : []
  const related =
    data?.related && typeof data.related === 'object' && !Array.isArray(data.related)
      ? data.related
      : {}
  return { history, related }
})

function onRecordsCount(n) {
  recordsCount.value = Number(n) || 0
}
</script>

<i18n>
{
  "ru": {
    "recordsCount": "Записей: {n}",
    "exportFailed": "Не удалось сформировать выгрузку",
    "downloadFailed": "Ошибка при скачивании",
    "auditTrailFileName": "Отчёт Контрольный след от {date}"
  },
  "en": {
    "recordsCount": "Records: {n}",
    "exportFailed": "Failed to generate export.",
    "downloadFailed": "Download failed.",
    "auditTrailFileName": "Audit Trail report from {date}"
  }
}
</i18n>
